import { ref, onUnmounted } from "vue";
import type { PDFViewer, EventBus } from "pdfjs-dist/web/pdf_viewer.mjs";
import moment from "moment";
import DateTimeUtils from "@utils/datetime.utils.ts";

export interface SnipRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface PdfViewerCaptureInstances {
  pdfViewer: PDFViewer;
  eventBus: InstanceType<typeof EventBus>;
}

export function usePdfViewerCapture() {
  const currentPage = ref(1);
  const isRendering = ref(false);
  const isSnipping = ref(false);
  const snipStart = ref<{ x: number; y: number } | null>(null);
  const snipEnd = ref<{ x: number; y: number } | null>(null);

  let _pdfViewer: PDFViewer | null = null;
  let _eventBus: InstanceType<typeof EventBus> | null = null;
  let _renderingFallbackTimer: ReturnType<typeof setTimeout> | null = null;

  // ─── Event handlers ────────────────────────────────────────────────────────

  const clearRenderingFallback = () => {
    if (_renderingFallbackTimer !== null) {
      clearTimeout(_renderingFallbackTimer);
      _renderingFallbackTimer = null;
    }
  };

  const onPageChanging = ({ pageNumber }: { pageNumber: number }) => {
    currentPage.value = pageNumber;
    isRendering.value = true;
    // Fallback: if pagerendered never fires (cached page), unblock after 500 ms
    clearRenderingFallback();
    _renderingFallbackTimer = setTimeout(() => {
      isRendering.value = false;
    }, 500);
  };

  const onPageRendered = ({ pageNumber }: { pageNumber: number }) => {
    if (pageNumber === currentPage.value) {
      clearRenderingFallback();
      isRendering.value = false;
    }
  };

  const onPagesLoaded = () => {
    currentPage.value = _pdfViewer?.currentPageNumber ?? 1;
    clearRenderingFallback();
    isRendering.value = false;
  };

  // ─── Init & cleanup ────────────────────────────────────────────────────────

  const init = ({ pdfViewer, eventBus }: PdfViewerCaptureInstances) => {
    _pdfViewer = pdfViewer;
    _eventBus = eventBus;

    eventBus.on("pagechanging", onPageChanging);
    eventBus.on("pagerendered", onPageRendered);
    eventBus.on("pagesloaded", onPagesLoaded);

    // Sync initial state — pagesinit has already fired by the time init() is called
    if (pdfViewer.currentPageNumber) {
      currentPage.value = pdfViewer.currentPageNumber;
    }
  };

  const cleanup = () => {
    clearRenderingFallback();
    if (!_eventBus) return;
    _eventBus.off("pagechanging", onPageChanging);
    _eventBus.off("pagerendered", onPageRendered);
    _eventBus.off("pagesloaded", onPagesLoaded);
    _pdfViewer = null;
    _eventBus = null;
  };

  onUnmounted(cleanup);

  // ─── Canvas access ─────────────────────────────────────────────────────────

  const getCurrentCanvas = (): HTMLCanvasElement | null => {
    if (!_pdfViewer) return null;
    const pageView = _pdfViewer.getPageView(currentPage.value - 1);
    return (pageView?.canvas as HTMLCanvasElement) ?? null;
  };

  // ─── Capture ───────────────────────────────────────────────────────────────

  /**
   * Capture the full current page as a JPEG data URL.
   */
  const captureCurrentPage = (): string | null => {
    const canvas = getCurrentCanvas();
    if (!canvas) return null;
    return canvas.toDataURL("image/jpeg", 0.92);
  };

  /**
   * Snip a rectangular region (in canvas pixel coords) as a JPEG data URL.
   */
  const captureSnip = (rect: SnipRect): string | null => {
    const canvas = getCurrentCanvas();
    if (!canvas) return null;

    const tempCanvas = document.createElement("canvas");
    tempCanvas.width = Math.round(rect.w);
    tempCanvas.height = Math.round(rect.h);
    const ctx = tempCanvas.getContext("2d");
    if (!ctx) return null;

    ctx.drawImage(
      canvas,
      Math.round(rect.x),
      Math.round(rect.y),
      Math.round(rect.w),
      Math.round(rect.h),
      0,
      0,
      Math.round(rect.w),
      Math.round(rect.h),
    );
    return tempCanvas.toDataURL("image/jpeg", 0.92);
  };

  // ─── Filename generation ───────────────────────────────────────────────────

  /**
   * Generate filename: {first 50 chars of PDF name}_Page {N}_{DD_MMM_YYYYHHmmss}.jpg
   * e.g. "tracemonkey_Page_1_21_Apr_2026062322.jpg"
   * Timestamp uses Singapore time (GMT+8), 24-hour clock.
   */
  const generateFilename = (pdfName: string, pageNumber: number): string => {
    const base = pdfName.replace(/\.pdf$/i, "").substring(0, 50);
    const ts = moment(DateTimeUtils.getServerTime()).format("DD_MMM_YYYYHHmmss");
    return `${base}_Page_${pageNumber}_${ts}.jpg`;
  };

  return {
    currentPage,
    isRendering,
    isSnipping,
    snipStart,
    snipEnd,
    init,
    cleanup,
    getCurrentCanvas,
    captureCurrentPage,
    captureSnip,
    generateFilename,
  };
}
